/**
 * Web observability — Sentry (errors) + PostHog (product analytics) + Clarity (heatmaps).
 * All three are dormant when their env vars are unset.
 *
 * Env (Vite-prefixed so they reach the browser):
 *   VITE_SENTRY_DSN           — Sentry browser DSN
 *   VITE_SENTRY_ENVIRONMENT   — defaults to MODE
 *   VITE_POSTHOG_KEY          — PostHog project key (phc_...)
 *   VITE_POSTHOG_HOST         — e.g. https://eu.i.posthog.com
 *   VITE_CLARITY_PROJECT_ID   — Clarity 10-char project id
 *   VITE_RELEASE              — git SHA (optional)
 */
import * as Sentry from "@sentry/react"
import posthog     from "posthog-js"

const ENV     = import.meta.env.VITE_SENTRY_ENVIRONMENT ?? import.meta.env.MODE
const RELEASE = import.meta.env.VITE_RELEASE

let sentryReady  = false
let posthogReady = false

export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN
  if (!dsn || sentryReady) return
  Sentry.init({
    dsn,
    environment: ENV,
    release:     RELEASE,
    tracesSampleRate: ENV === "production" ? 0.1 : 1.0,
    replaysSessionSampleRate: 0,        // don't record every session
    replaysOnErrorSampleRate: 1.0,      // record when an error happens
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllText: false, blockAllMedia: true }),
    ],
    beforeSend(event) {
      // Strip auth headers / cookies if Sentry captured them
      if (event.request?.headers) {
        delete (event.request.headers as Record<string, unknown>).Authorization
        delete (event.request.headers as Record<string, unknown>).Cookie
      }
      return event
    },
  })
  sentryReady = true
}

export function initPosthog(): void {
  const key  = import.meta.env.VITE_POSTHOG_KEY
  const host = import.meta.env.VITE_POSTHOG_HOST ?? "https://eu.i.posthog.com"
  if (!key || posthogReady) return
  posthog.init(key, {
    api_host: host,
    person_profiles: "identified_only",
    capture_pageview: true,
    capture_pageleave: true,
    autocapture: true,
    session_recording: {
      maskAllInputs: true,       // PHI hygiene: mask form inputs by default
      maskTextSelector: "[data-ph-mask]",
    },
  })
  posthogReady = true
}

export function initClarity(): void {
  const id = import.meta.env.VITE_CLARITY_PROJECT_ID
  if (!id || typeof window === "undefined") return
  if ((window as unknown as { clarity?: unknown }).clarity) return
  const w = window as unknown as Record<string, unknown> & { clarity?: (...a: unknown[]) => void }
  ;(function (c: Window, l: Document, a: string, r: string, i: string) {
    ;(c as unknown as Record<string, unknown>)[a] = (c as unknown as Record<string, unknown>)[a] ||
      function (...args: unknown[]) {
        ;(((c as unknown as Record<string, unknown>)[a] as unknown as { q: unknown[][] }).q ||=
          []).push(args)
      }
    const t = l.createElement(r) as HTMLScriptElement
    t.async = true
    t.src = "https://www.clarity.ms/tag/" + i
    const y = l.getElementsByTagName(r)[0]
    y?.parentNode?.insertBefore(t, y)
  })(window, document, "clarity", "script", id)
  // Attach so we can tag page context later
  void w.clarity
}

/** Identify the logged-in user across all 3 tools. Call after auth resolves. */
export function identifyUser(u: { id: string; email?: string; tenantId?: string; role?: string }) {
  if (sentryReady) {
    Sentry.setUser({ id: u.id, email: u.email })
    if (u.tenantId) Sentry.setTag("tenantId", u.tenantId)
    if (u.role)     Sentry.setTag("role", u.role)
  }
  if (posthogReady) {
    posthog.identify(u.id, {
      email:    u.email,
      tenantId: u.tenantId,
      role:     u.role,
    })
  }
  const clarity = (window as unknown as { clarity?: (cmd: string, ...a: unknown[]) => void }).clarity
  if (clarity) {
    clarity("identify", u.id)
    if (u.tenantId) clarity("set", "tenantId", u.tenantId)
    if (u.role)     clarity("set", "role", u.role)
  }
}

export function initObservability(): void {
  initSentry()
  initPosthog()
  initClarity()
}

/** Capture a product-analytics event. No-op when PostHog is not initialized. */
export function track(event: string, properties?: Record<string, unknown>) {
  if (!posthogReady) return
  try { posthog.capture(event, properties) }
  catch { /* analytics must never break the UI */ }
}

export function captureError(err: unknown, ctx?: Record<string, unknown>) {
  if (sentryReady) Sentry.captureException(err, { extra: ctx })
  else console.error(err, ctx)
}

export { Sentry, posthog }
